import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { api } from "../../../lib/axios";
import {
  createPlanAbonnement,
  updatePlanAbonnement,
  type PlanAbonnement,
  type Devise,
  type Frequence,
  type PlanType,
} from "../../../lib/api/plansAbonnement";
import { PlanSummarySticky } from "./PlanSummarySticky";

type Mode = "create" | "edit" | "duplicate";
type ServiceType = "NETTOYAGE" | "DESINFECTION" | "LAVAGE_BAC";
type FournitureRule =
  | "UNIQUE_DEMARRAGE"
  | "RECURRENT_MENSUEL"
  | "SUR_DEMANDE"
  | "REMPLACEMENT_CASSE"
  | "PAR_PASSAGE";

const freqs: Frequence[] = ["PONCTUEL", "HEBDOMADAIRE", "MENSUEL", "ANNUEL"];
const planTypes: Array<{ value: PlanType; label: string; description: string }> = [
  {
    value: "MENAGE",
    label: "Ménage",
    description: "Client résidentiel avec fréquence et dotations standards.",
  },
  {
    value: "ENTREPRISE",
    label: "Entreprise",
    description: "Entreprise standard avec contrat récurrent et besoins structurés.",
  },
  {
    value: "PROFESSIONNEL",
    label: "Professionnel",
    description: "Entreprise ou commerce avec besoins récurrents et volumes moyens.",
  },
  {
    value: "CHANTIER",
    label: "Chantier",
    description: "Interventions temporaires ou déchets intensifs à forte variabilité.",
  },
];

const SERVICES: Array<{ type: ServiceType; label: string }> = [
  { type: "NETTOYAGE", label: "Nettoyage" },
  { type: "DESINFECTION", label: "Désinfection" },
  { type: "LAVAGE_BAC", label: "Lavage bac" },
];

type ServiceEnabledMap = Record<ServiceType, boolean>;
type ServicePriceMap = Record<ServiceType, string>;
type ZoneOption = {
  id: number;
  nom: string;
  actif?: boolean;
};

type CatalogItemOption = {
  id: number;
  code: string;
  nom: string;
  displayName: string;
  family?: string | null;
  category?: string | null;
  isDotationEligible?: boolean;
  isActive?: boolean;
  stockDisponible: number;
};

type PlanDotationLine = {
  key: string;
  resourceCatalogId: string;
  quantite: string;
  isRequired: boolean;
  regleFourniture: FournitureRule;
};

function defaultRegleForCatalogItem(item: CatalogItemOption | undefined): FournitureRule {
  const fam = String(item?.family ?? "").toUpperCase();
  const nom = String(item?.nom ?? item?.displayName ?? "").toUpperCase();
  if (fam.includes("SAC") || nom.includes("SAC")) return "PAR_PASSAGE";
  if (fam.includes("BAC") || fam.includes("POUB") || nom.includes("BAC") || nom.includes("POUBELLE")) {
    return "UNIQUE_DEMARRAGE";
  }
  return "SUR_DEMANDE";
}

export type PlansAbonnementModalProps = {
  open: boolean;
  onClose: () => void;
  mode: Mode;
  initial: PlanAbonnement | null;
  onSaved: () => void;
};

function initialDotationLines(initial: PlanAbonnement | null): PlanDotationLine[] {
  return (initial?.dotations ?? [])
    .filter((d) => d.resourceCatalogId > 0)
    .map((d) => ({
      key: String(d.id ?? `${d.resourceCatalogId}-${Math.random()}`),
      resourceCatalogId: String(d.resourceCatalogId),
      quantite: String(d.quantite ?? 1),
      isRequired: d.isRequired !== false,
      regleFourniture: (d.regleFourniture ?? "SUR_DEMANDE") as FournitureRule,
    }));
}

function initialServicesEnabled(initial: PlanAbonnement | null): ServiceEnabledMap {
  return {
    NETTOYAGE: Boolean(initial?.services?.find((s) => s.type === "NETTOYAGE" && s.actif)),
    DESINFECTION: Boolean(initial?.services?.find((s) => s.type === "DESINFECTION" && s.actif)),
    LAVAGE_BAC: Boolean(initial?.services?.find((s) => s.type === "LAVAGE_BAC" && s.actif)),
  };
}

function initialServicePrice(initial: PlanAbonnement | null): ServicePriceMap {
  const nettoyage = initial?.services?.find((s) => s.type === "NETTOYAGE")?.prixSupplementaire;
  const desinfection = initial?.services?.find((s) => s.type === "DESINFECTION")?.prixSupplementaire;
  const lavage = initial?.services?.find((s) => s.type === "LAVAGE_BAC")?.prixSupplementaire;
  return {
    NETTOYAGE: nettoyage != null ? String(nettoyage) : "",
    DESINFECTION: desinfection != null ? String(desinfection) : "",
    LAVAGE_BAC: lavage != null ? String(lavage) : "",
  };
}

type PlanPreset = {
  passagesParMois: string;
  frequence: Frequence;
  servicesEnabled: ServiceEnabledMap;
};

function buildPreset(type: PlanType): PlanPreset {
  if (type === "PROFESSIONNEL") {
    return {
      passagesParMois: "8",
      frequence: "MENSUEL",
      servicesEnabled: { NETTOYAGE: true, DESINFECTION: false, LAVAGE_BAC: false },
    };
  }
  if (type === "CHANTIER") {
    return {
      passagesParMois: "12",
      frequence: "MENSUEL",
      servicesEnabled: { NETTOYAGE: false, DESINFECTION: false, LAVAGE_BAC: true },
    };
  }
  return {
    passagesParMois: "4",
    frequence: "MENSUEL",
    servicesEnabled: { NETTOYAGE: false, DESINFECTION: false, LAVAGE_BAC: false },
  };
}

export function PlansAbonnementModal({
  open,
  onClose,
  mode,
  initial,
  onSaved,
}: PlansAbonnementModalProps): React.JSX.Element | null {
  const [loading, setLoading] = useState(false);
  const [planType, setPlanType] = useState<PlanType>(initial?.type ?? "MENAGE");
  const [nom, setNom] = useState(initial?.nom ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [actif, setActif] = useState(initial?.actif ?? true);
  const [zoneId, setZoneId] = useState(initial?.zoneId != null ? String(initial.zoneId) : "");
  const [zonesLoading, setZonesLoading] = useState(false);
  const [zones, setZones] = useState<ZoneOption[]>([]);
  const [montant, setMontant] = useState(String(initial?.prixBase ?? initial?.montant ?? ""));
  const [devise, setDevise] = useState<Devise>(initial?.devise ?? "USD");
  const [frequence, setFrequence] = useState<Frequence>(initial?.frequence ?? "MENSUEL");
  const [passagesParMois, setPassagesParMois] = useState(String(initial?.passagesParMois ?? 4));
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogItems, setCatalogItems] = useState<CatalogItemOption[]>([]);
  const [dotationLines, setDotationLines] = useState<PlanDotationLine[]>(initialDotationLines(initial));
  const [servicesEnabled, setServicesEnabled] = useState(initialServicesEnabled(initial));
  const [servicePrix, setServicePrix] = useState(initialServicePrice(initial));
  const [isCustomized, setIsCustomized] = useState(false);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        setZonesLoading(true);
        const { data } = await api.get<unknown>("/zones");
        const rows = Array.isArray(data)
          ? data
          : Array.isArray((data as { data?: unknown[] })?.data)
            ? ((data as { data?: unknown[] }).data ?? [])
            : [];
        const next = rows
          .filter((r): r is Record<string, unknown> => !!r && typeof r === "object")
          .map((r) => ({
            id: Number(r.id),
            nom: String(r.nom ?? ""),
            actif: typeof r.actif === "boolean" ? r.actif : undefined,
          }))
          .filter((z) => Number.isFinite(z.id) && z.id > 0 && z.nom.trim().length > 0 && z.actif !== false);
        if (mounted) setZones(next);
      } catch {
        if (mounted) setZones([]);
      } finally {
        if (mounted) setZonesLoading(false);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        setCatalogLoading(true);
        const { data } = await api.get<unknown>("/resources/catalog", { params: { actif: true } });
        const rows = Array.isArray(data)
          ? data
          : Array.isArray((data as { data?: unknown[] })?.data)
            ? ((data as { data?: unknown[] }).data ?? [])
            : [];
        const next = rows
          .filter((r): r is Record<string, unknown> => !!r && typeof r === "object")
          .map((r) => {
            const stock = r.stock as Record<string, unknown> | null | undefined;
            const onHand = Number(stock?.calculated ?? r.stockActuel ?? 0);
            const reserved = Number(stock?.reserved ?? r.reserve ?? 0);
            return {
              id: Number(r.id),
              code: String(r.code ?? ""),
              nom: String(r.displayName ?? r.nom ?? ""),
              displayName: String(r.displayName ?? r.nom ?? ""),
              family: r.family == null ? r.famille == null ? null : String(r.famille) : String(r.family),
              category: r.category == null ? null : String(r.category),
              isDotationEligible: r.isDotationEligible === true,
              isActive: r.isActive !== false && r.actif !== false,
              stockDisponible: Math.max(0, onHand - reserved),
            };
          })
          .filter((item) => Number.isFinite(item.id) && item.id > 0 && item.nom.trim().length > 0 && item.isActive !== false && item.category !== "ROLLING_EQUIPMENT" && item.isDotationEligible === true);
        if (mounted) setCatalogItems(next);
      } catch {
        if (mounted) setCatalogItems([]);
      } finally {
        if (mounted) setCatalogLoading(false);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, []);

  const applyPlanTypePreset = (nextType: PlanType) => {
    const preset = buildPreset(nextType);
    setPassagesParMois(preset.passagesParMois);
    setFrequence(preset.frequence);
    setServicesEnabled((prev) => ({
      NETTOYAGE: preset.servicesEnabled.NETTOYAGE,
      DESINFECTION: preset.servicesEnabled.DESINFECTION,
      LAVAGE_BAC: preset.servicesEnabled.LAVAGE_BAC,
      // keep explicit user-added services if already active and customized
      ...(isCustomized ? { ...prev } : {}),
    }));
  };

  const handleTypeChange = (nextType: PlanType) => {
    if (nextType === planType) return;
    if (isCustomized) {
      const ok = window.confirm(
        "Changer le type va appliquer un preset (passages/fréquence/dotations/services suggérés). Continuer ?"
      );
      if (!ok) return;
    }
    setPlanType(nextType);
    applyPlanTypePreset(nextType);
    setIsCustomized(false);
  };

  const summary = useMemo(() => {
    const base = Number(String(montant).replace(",", "."));
    const prixBase = Number.isFinite(base) ? base : 0;
    const supplements = (Object.keys(servicesEnabled) as ServiceType[]).reduce((acc, key) => {
      if (!servicesEnabled[key]) return acc;
      const p = Number(String(servicePrix[key]).replace(",", "."));
      return acc + (Number.isFinite(p) ? Math.max(0, p) : 0);
    }, 0);
    return {
      prixBase,
      supplements,
      total: Math.max(0, prixBase + supplements),
      dotations: dotationLines.map((line) => {
        const item = catalogItems.find((c) => String(c.id) === line.resourceCatalogId);
        return `${item?.nom ?? "Article catalogue"} x${Math.max(1, Number(line.quantite || 1))}`;
      }),
      services: SERVICES.filter((s) => servicesEnabled[s.type]).map((s) => s.label),
    };
  }, [montant, servicesEnabled, servicePrix, dotationLines, catalogItems]);

  const blockingErrors = useMemo(() => {
    const out: string[] = [];
    const prixBase = Number(String(montant).replace(",", "."));
    const ppm = Number(passagesParMois);
    if (!nom.trim()) out.push("Nom du plan requis");
    if (!Number.isFinite(prixBase) || prixBase <= 0) out.push("Prix manquant");
    if (!Number.isInteger(ppm) || ppm < 1) out.push("Aucun passage défini");
    return out;
  }, [nom, montant, passagesParMois]);

  const selectedZoneLabel = useMemo(() => {
    if (!zoneId) return "Toutes les zones";
    const current = zones.find((z) => String(z.id) === zoneId);
    return current?.nom ?? "Zone spécifique";
  }, [zoneId, zones]);

  const save = async () => {
    try {
      const prixBase = Number(String(montant).replace(",", "."));
      const ppm = Number(passagesParMois);
      const parsedZoneId = zoneId.trim() ? Number(zoneId) : null;

      if (!nom.trim()) return toast.warning("Nom obligatoire.");
      if (!planType) return toast.warning("Type de plan obligatoire.");
      if (!Number.isFinite(prixBase) || prixBase <= 0) return toast.warning("prixBase doit être > 0.");
      if (!Number.isInteger(ppm) || ppm < 1) return toast.warning("passagesParMois doit être >= 1.");
      if (parsedZoneId != null && (!Number.isInteger(parsedZoneId) || parsedZoneId <= 0)) {
        return toast.warning("Zone invalide.");
      }

      const dotations = dotationLines.map((line) => {
        const resourceCatalogId = Number(line.resourceCatalogId);
        const quantite = Number(line.quantite);
        if (!Number.isInteger(resourceCatalogId) || resourceCatalogId <= 0) {
          throw new Error("Sélectionnez un article catalogue pour chaque dotation.");
        }
        if (!Number.isInteger(quantite) || quantite < 1) {
          throw new Error("Quantité de dotation invalide.");
        }
        return {
          resourceCatalogId,
          quantite,
          regleFourniture: line.regleFourniture,
          isRequired: line.isRequired,
        };
      });

      const services = (Object.keys(servicesEnabled) as ServiceType[])
        .filter((k) => servicesEnabled[k])
        .map((type) => {
          const raw = servicePrix[type].trim();
          const prixSupplementaire = raw ? Number(raw.replace(",", ".")) : null;
          if (prixSupplementaire != null && (!Number.isFinite(prixSupplementaire) || prixSupplementaire < 0)) {
            throw new Error(`Supplément invalide pour ${type}.`);
          }
          return { type, actif: true, prixSupplementaire };
        });

      const payload = {
        nom: nom.trim(),
        type: planType,
        description: description.trim() || null,
        montant: prixBase,
        devise,
        frequence,
        passagesParMois: ppm,
        zoneId: parsedZoneId,
        actif,
        dotations,
        services,
      };

      setLoading(true);
      if (mode === "edit" && initial?.id) await updatePlanAbonnement(initial.id, payload);
      else await createPlanAbonnement(payload);
      onSaved();
      onClose();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Erreur enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
      <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl sm:max-h-[90vh]">
        <header className="flex shrink-0 items-start justify-between border-b border-slate-800 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {mode === "edit" ? "Modifier le plan" : "Nouveau plan"}
            </h2>
            <p className="text-xs text-slate-400">Les passages par mois définissent la collecte incluse.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-800">
            <X className="h-5 w-5" />
          </button>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto px-5 py-4 pb-8">
          <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
            <div className="space-y-4">
              <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <p className="text-xs text-slate-400">Informations plan</p>
                <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <input
                    value={nom}
                    onChange={(e) => {
                      setNom(e.target.value);
                      setIsCustomized(true);
                    }}
                    className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
                    placeholder="Nom plan *"
                  />
                  <div>
                    <select
                      value={planType}
                      onChange={(e) => handleTypeChange(e.target.value as PlanType)}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
                    >
                      <option value="MENAGE">Ménage</option>
                      <option value="ENTREPRISE">Entreprise</option>
                      <option value="PROFESSIONNEL">Professionnel</option>
                      <option value="CHANTIER">Chantier</option>
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                      {planTypes.find((p) => p.value === planType)?.description}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <select
                      value={zoneId}
                      onChange={(e) => {
                        setZoneId(e.target.value);
                        setIsCustomized(true);
                      }}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
                    >
                      <option value="">Toutes les zones (offre globale)</option>
                      {zones.map((z) => (
                        <option key={z.id} value={String(z.id)}>
                          {z.nom}
                        </option>
                      ))}
                    </select>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">
                        {zonesLoading ? "Chargement des zones..." : zones.length ? `${zones.length} zone(s) active(s)` : "Aucune zone disponible"}
                      </span>
                      <Link to="/admin/zones" className="text-emerald-300 hover:text-emerald-200">
                        Gérer les zones
                      </Link>
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={actif}
                      onChange={(e) => {
                        setActif(e.target.checked);
                        setIsCustomized(true);
                      }}
                      className="accent-emerald-500"
                    />
                    Plan actif
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setIsCustomized(true);
                    }}
                    className="md:col-span-2 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
                    placeholder="Description"
                  />
                </div>
              </section>

              <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <p className="text-xs text-slate-400">Facturation</p>
                <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <input
                    value={montant}
                    onChange={(e) => {
                      setMontant(e.target.value);
                      setIsCustomized(true);
                    }}
                    className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
                    placeholder="Prix de base *"
                  />
                  <select
                    value={devise}
                    onChange={(e) => {
                      setDevise(e.target.value as Devise);
                      setIsCustomized(true);
                    }}
                    className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
                  >
                    <option value="USD">USD</option>
                    <option value="CDF">CDF</option>
                  </select>
                  <select
                    value={frequence}
                    onChange={(e) => {
                      setFrequence(e.target.value as Frequence);
                      setIsCustomized(true);
                    }}
                    className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
                  >
                    {freqs.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                  <input
                    value={passagesParMois}
                    onChange={(e) => {
                      setPassagesParMois(e.target.value.replace(/\D/g, ""));
                      setIsCustomized(true);
                    }}
                    className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
                    placeholder="Passages/mois *"
                  />
                </div>
              </section>

              <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs text-slate-400">Dotations incluses</p>
                    <p className="mt-1 text-xs text-slate-500">
                      <strong className="text-slate-300">Bac / poubelle 120 L</strong> : offert à la première souscription
                      (règle « Unique au démarrage »).{" "}
                      <strong className="text-slate-300">Sac biodégradable</strong> : 1 offert à chaque collecte (règle
                      « À chaque collecte »). Chaque ligne du plan a sa propre règle.
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={catalogItems.length === 0}
                    onClick={() => {
                      setDotationLines((prev) => [
                        ...prev,
                        {
                          key: `${Date.now()}-${prev.length}`,
                          resourceCatalogId: "",
                          quantite: "1",
                          isRequired: true,
                          regleFourniture: "SUR_DEMANDE",
                        },
                      ]);
                      setIsCustomized(true);
                    }}
                    className="rounded-xl border border-emerald-600/50 px-3 py-2 text-xs font-semibold text-emerald-200 hover:bg-emerald-950/30 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    + Ajouter une dotation
                  </button>
                </div>

                {catalogItems.length === 0 ? (
                  <div className="mt-3 rounded-xl border border-amber-700/40 bg-amber-950/20 p-4 text-sm text-amber-100">
                    <p className="font-semibold">Aucun article catalogue disponible.</p>
                    <p className="mt-1 text-xs text-amber-100/80">
                      Créez d’abord vos produits : poubelles, bacs, sacs, EPI…
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link to="/admin/ressources?tab=stockCatalogue" className="rounded-lg border border-amber-500/50 px-3 py-1.5 text-xs text-amber-100">
                        Créer article catalogue
                      </Link>
                      <Link to="/admin/ressources?tab=stockCatalogue" className="rounded-lg border border-emerald-500/50 px-3 py-1.5 text-xs text-emerald-100">
                        Faire entrée stock
                      </Link>
                    </div>
                  </div>
                ) : null}

                <div className="mt-3 space-y-3">
                  {dotationLines.map((line, idx) => {
                    const selected = catalogItems.find((item) => String(item.id) === line.resourceCatalogId);
                    return (
                      <div key={line.key} className="grid gap-3 rounded-xl border border-slate-800 bg-slate-950 p-3 md:grid-cols-[1fr_110px_auto]">
                        <div>
                          <select
                            value={line.resourceCatalogId}
                            onChange={(e) => {
                              const value = e.target.value;
                              const item = catalogItems.find((i) => String(i.id) === value);
                              setDotationLines((prev) =>
                                prev.map((l) =>
                                  l.key === line.key
                                    ? {
                                        ...l,
                                        resourceCatalogId: value,
                                        regleFourniture: value
                                          ? defaultRegleForCatalogItem(item)
                                          : l.regleFourniture,
                                      }
                                    : l
                                )
                              );
                              setIsCustomized(true);
                            }}
                            className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-white"
                          >
                            <option value="">Sélectionner un article catalogue</option>
                            {catalogItems.map((item) => (
                              <option key={item.id} value={String(item.id)}>
                                {item.code} — {item.displayName} {item.family ? `— ${item.family}` : ""} — Stock : {item.stockDisponible}
                              </option>
                            ))}
                          </select>
                          {selected?.stockDisponible === 0 ? (
                            <p className="mt-1 text-[11px] text-amber-300">Article sans stock disponible actuellement.</p>
                          ) : null}
                        </div>
                        <input
                          value={line.quantite}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setDotationLines((prev) => prev.map((l) => (l.key === line.key ? { ...l, quantite: value } : l)));
                            setIsCustomized(true);
                          }}
                          className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-white"
                          placeholder="Qté"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setDotationLines((prev) => prev.filter((l) => l.key !== line.key));
                            setIsCustomized(true);
                          }}
                          className="rounded-xl border border-rose-700/50 px-3 py-2 text-xs text-rose-200 hover:bg-rose-950/30"
                        >
                          Supprimer
                        </button>
                        <label className="md:col-span-3 inline-flex items-center gap-2 text-xs text-slate-400">
                          <input
                            type="checkbox"
                            checked={line.isRequired}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setDotationLines((prev) => prev.map((l) => (l.key === line.key ? { ...l, isRequired: checked } : l)));
                              setIsCustomized(true);
                            }}
                            className="accent-emerald-500"
                          />
                          Dotation obligatoire #{idx + 1}
                        </label>
                        <div className="md:col-span-3">
                          <label className="text-[11px] text-slate-500">Règle de fourniture</label>
                          <select
                            value={line.regleFourniture}
                            onChange={(e) => {
                              const regle = e.target.value as FournitureRule;
                              setDotationLines((prev) =>
                                prev.map((l) => (l.key === line.key ? { ...l, regleFourniture: regle } : l))
                              );
                              setIsCustomized(true);
                            }}
                            className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white"
                          >
                            <option value="PAR_PASSAGE">À chaque collecte (1 par passage) — sacs</option>
                            <option value="UNIQUE_DEMARRAGE">Unique au démarrage — bac / poubelle</option>
                            <option value="RECURRENT_MENSUEL">Récurrent mensuel</option>
                            <option value="SUR_DEMANDE">Sur demande</option>
                            <option value="REMPLACEMENT_CASSE">Remplacement cassé / perdu</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {catalogLoading ? <p className="mt-2 text-xs text-slate-500">Chargement du catalogue…</p> : null}
              </section>

              <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <p className="text-xs text-slate-400">Services optionnels</p>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {SERVICES.map((s) => (
                    <div key={s.type} className="rounded-xl border border-slate-800 bg-slate-950 p-3">
                      <label className="inline-flex items-center gap-2 text-sm text-slate-200">
                        <input
                          type="checkbox"
                          checked={servicesEnabled[s.type]}
                          onChange={(e) => {
                            setServicesEnabled((prev) => ({ ...prev, [s.type]: e.target.checked }));
                            setIsCustomized(true);
                          }}
                          className="accent-emerald-500"
                        />
                        {s.label}
                      </label>
                      <input
                        value={servicePrix[s.type]}
                        onChange={(e) => {
                          setServicePrix((prev) => ({ ...prev, [s.type]: e.target.value }));
                          setIsCustomized(true);
                        }}
                        disabled={!servicesEnabled[s.type]}
                        className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-white disabled:opacity-50"
                        placeholder="Supplément"
                      />
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <PlanSummarySticky
              planType={planType}
              zoneLabel={selectedZoneLabel}
              frequence={frequence}
              passagesParMois={Math.max(0, Number(passagesParMois || 0))}
              dotations={summary.dotations}
              services={summary.services}
              prixBase={summary.prixBase}
              supplements={summary.supplements}
              totalMensuel={summary.total}
              devise={devise}
              blockers={blockingErrors}
            />
          </div>
        </main>

        <footer className="relative z-10 shrink-0 border-t border-slate-800 bg-slate-950/95 p-4 backdrop-blur-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-xl border border-slate-700 px-4 py-2 text-slate-200 sm:w-auto"
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={loading || blockingErrors.length > 0}
              onClick={() => void save()}
              className="w-full rounded-xl bg-emerald-500 px-4 py-2 font-semibold text-slate-950 disabled:opacity-60 sm:w-auto"
            >
              {loading ? "Enregistrement..." : mode === "edit" ? "Enregistrer" : "Créer le plan"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

